import { useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { adminAPI } from '@/lib/api';
import { usePagination, TablePagination } from '@/components/admin/TablePagination';

const METHOD_COLORS = {
  POST: 'bg-teal/15 text-teal border-teal/30',
  PUT: 'bg-gold/15 text-gold border-gold/30',
  PATCH: 'bg-gold/15 text-gold border-gold/30',
  DELETE: 'bg-destructive/15 text-destructive border-destructive/30',
};

export default function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const logsPg = usePagination(logs, 15);

  const load = () => {
    setLoading(true);
    adminAPI.listAuditLogs(300)
      .then(r => setLogs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-audit-log" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground flex items-center gap-3">
            <ShieldCheck className="h-7 w-7 text-teal" /> Audit Log
          </h1>
          <p className="font-body text-sm text-foreground/60 mt-1">
            A trail of every administrative change — who did what, and when.
          </p>
        </div>
        <Button
          variant="outline"
          data-testid="audit-refresh-btn"
          onClick={load}
          className="rounded-lg border-border font-body text-sm gap-2"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="rounded-2xl bg-card border border-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-foreground/50">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center font-body text-sm text-foreground/50" data-testid="audit-empty">
            No admin actions recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border font-body text-xs uppercase tracking-wider text-foreground/50">
                  <th className="px-5 py-3">When</th>
                  <th className="px-5 py-3">Admin</th>
                  <th className="px-5 py-3">Action</th>
                  <th className="px-5 py-3">Endpoint</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {logsPg.pageItems.map((log, i) => (
                  <tr key={i} data-testid={`audit-row-${i}`} className="border-b border-border/50 hover:bg-muted/40 transition-colors">
                    <td className="px-5 py-3 font-body text-xs text-foreground/70 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg' })}
                    </td>
                    <td className="px-5 py-3 font-body text-sm text-foreground">{log.admin_email || '—'}</td>
                    <td className="px-5 py-3">
                      <Badge variant="outline" className={`rounded-full font-body text-[11px] ${METHOD_COLORS[log.method] || 'bg-muted text-foreground/70'}`}>
                        {log.method}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 font-body text-xs text-foreground/70 font-mono">{log.path}</td>
                    <td className="px-5 py-3 font-body text-xs text-foreground/60">{log.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!loading && logs.length > 0 && <TablePagination {...logsPg} testid="audit-pagination" />}
    </div>
  );
}
